import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Per-device anonymous "like" on a proof. Visitor token persists in
// localStorage; one like per (proof, token) is enforced server-side.
//
// On mount we read the count from the server (and the per-device liked
// state from localStorage). On click we optimistically increment, then
// POST. The server treats double-likes as idempotent so a stale state
// won't double-count.
const TOKEN_KEY = "shosay-visitor-token";
const likedKey = (uuid) => `shosay-liked-${uuid}`;

const getOrCreateToken = () => {
  if (typeof window === "undefined") return "";
  try {
    let t = window.localStorage.getItem(TOKEN_KEY);
    if (!t) {
      t = (window.crypto?.randomUUID?.() || `v-${Math.random().toString(36).slice(2)}-${Date.now()}`);
      window.localStorage.setItem(TOKEN_KEY, t);
    }
    return t;
  } catch (e) {
    // localStorage may be unavailable (private browsing, embedded contexts);
    // degrade gracefully — likes won't persist across reloads but still work
    // for the current session.
    return `ephemeral-${Math.random().toString(36).slice(2)}`;
  }
};

const HeartButton = ({ proof, host, accent = "#FF4D6D", className = "" }) => {
  const [count, setCount] = useState(proof.likes_count || 0);
  const [liked, setLiked] = useState(false);
  const [bumping, setBumping] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setLiked(window.localStorage.getItem(likedKey(proof.uuid)) === "1");
    } catch (e) { /* ignore */ }
  }, [proof.uuid]);

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (liked) return;

    setLiked(true);
    setCount((c) => c + 1);
    setBumping(true);
    setTimeout(() => setBumping(false), 400);

    try { window.localStorage.setItem(likedKey(proof.uuid), "1"); } catch (e) { /* ignore */ }

    const token = getOrCreateToken();
    const base = host || "";
    try {
      const res = await fetch(`${base}/api/v1/proofs/${proof.uuid}/likes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_token: token }),
      });
      if (res.ok) {
        const data = await res.json();
        if (typeof data.likes_count === "number") setCount(data.likes_count);
      }
    } catch (err) {
      // Optimistic update stays — failure to reach the server still gives
      // the visitor immediate feedback. No revert.
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={liked}
      className={`inline-flex items-center gap-1 text-xs font-bold transition-opacity ${liked ? "" : "hover:opacity-80"} ${className}`}
      aria-label={liked ? "Liked" : "Like this testimonial"}
      aria-pressed={liked}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.svg
          key={liked ? "filled" : "outline"}
          viewBox="0 0 24 24"
          className="w-4 h-4"
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: bumping ? 1.4 : 1, opacity: 1 }}
          exit={{ scale: 0.7, opacity: 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 18 }}
          fill={liked ? accent : "none"}
          stroke={liked ? accent : "currentColor"}
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"
          />
        </motion.svg>
      </AnimatePresence>
      <span>{count}</span>
    </button>
  );
};

export default HeartButton;
